import {Symbol, SymbolStates, WildStates} from "./Symbol";
import {gsap} from "gsap";
import {
    IFeatureData,
    IMonster,
    IMonsterWalk,
    IPosition,
    MONSTER_EXPRESSION,
    MONSTER_MOVE_ACTIONS,
    MONSTER_WEAPON_ACTIONS
} from "../server/IData";
import {Game} from "../Game";
import {GameAssets} from "../GameAssets";
import {Sound} from "../sound/Sound";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {GameModeNames} from "../server/GameModeNames";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";

export class MonsterSymbol extends Symbol {

    private readonly _amount: PIXI.BitmapText;
    private readonly _monsterIdx: number;
    private _airstrikeAnimation: PIXI.spine.Spine;
    private _monsterWalkIdx: number;
    private _isFacingLeft: boolean;
    private readonly _wins: PIXI.spine.Spine;
    private _prevWinsAnimName: string = "";
    private _grExecuteCount: number = 0;
    private _isAborted: boolean = false;
    private _stateAnim: PIXI.spine.core.TrackEntry | undefined;
    private readonly _extraFlagsText: PIXI.BitmapText;
    private _extraFlagsNum: number;
    private readonly _extraFlags: string[];

    constructor(symbolName: string, initState: SymbolStates, reelPosition: number, trackIdx: number = 0, loop: boolean = false) {
        super(symbolName, initState, reelPosition, trackIdx, loop);

        this._isMonster = true;

        this._isFacingLeft = true;

        this._wins = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("monsterWins"));
        this._wins.visible = false;

        this._amount = new PIXI.BitmapText("", {
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 20
        });
        this._amount.anchor = new PIXI.Point(0.5, 0.5);

        this._monsterIdx = Game.MONSTER_NAME_TO_IDX[symbolName];

        // #467
        this._extraFlags = Game.monsterExtraCarriedSymbols[this._monsterIdx];
        this._extraFlagsNum = 4;
        this._extraFlagsText = new PIXI.BitmapText("", {
            fontName: GameAssets.BITMAP_FONT_MULTI,
            fontSize: 24,
            letterSpacing: 10
        });
        this._extraFlagsText.anchor = new PIXI.Point(0.5, 0.5);
        this._extraFlagsText.x = 70;
        this._extraFlagsText.y = -55;

        this.addChild(this._extraFlagsText, this._amount);
        // if a monster is carrying weapons, show them if they are reSpawned
        this.flagsInit();
    }

    public highlightMonsterPath(path: IMonsterWalk[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            highlightedPos: number[] = this.getCollectedIdx(path),
            reelId: number = path[0].x;

        for (let idx of highlightedPos) {
            const xPos: number = Math.floor(idx / Game.TOTAL_REELS_NUM);
            const yPos: number = idx % Game.TOTAL_REELS_NUM;

            if (this.reelPosition !== yPos || reelId !== xPos) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[xPos][yPos],
                    symbol: Symbol = Game.reelsView.reelModels[xPos].getSymbolAt(yPos, reelsSymName);
                tl.add(symbol?.showHighlight(), 0);
            }
        }

        return tl;
    }

    public setMonsterWalkIdx(idx: number): void {
        this._monsterWalkIdx = idx;
    }

    public turnMonsterToRight(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (this._isFacingLeft) {
            tl.addLabel("turn");

            tl.add([
                this.getStateAnimation(MONSTER_MOVE_ACTIONS.TURN, 5),
                () => {
                    this._isFacingLeft = false;
                    Game.soundPlayer.playSound(Sound.TANK_U_TURN, 1);

                    this._extraFlagsText.x = -70;
                }
            ], "turn");
            tl.to(this.animation.scale, {
                x: -this.animation.scale.x,
                duration,
            }, "turn");
        }

        return tl;
    }

    public turnMonsterToLeft(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (!this._isFacingLeft) {
            tl.addLabel("turn");

            tl.add([
                this.getStateAnimation(MONSTER_MOVE_ACTIONS.TURN, 5),
                () => {
                    this._isFacingLeft = true;
                    Game.soundPlayer.playSound(Sound.TANK_U_TURN, 1);

                    this._extraFlagsText.x = 70;
                }
            ], "turn");
            tl.to(this.animation.scale, {
                x: Math.abs(this.animation.scale.x),
                duration,
            }, "turn");
        }

        return tl;
    }

    public turnMonsterAgainstWall(monsterX: number, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (monsterX === 0) {
            tl.add(this.turnMonsterToRight(duration));
        } else if (monsterX === Game.ACTIVE_REELS_NUM - 1) {
            tl.add(this.turnMonsterToLeft(duration));
        }
        return tl;
    }

    public turnMonsterTowardsTarget(monsterX: number, targetX: number, duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (targetX > monsterX) {
            tl.add(this.turnMonsterToRight(duration));
        } else if (targetX < monsterX) {
            tl.add(this.turnMonsterToLeft(duration));
        }
        return tl;
    }

    public driveBulldozer(path: IMonsterWalk[], monsterPath: IMonsterWalk[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            selfReelId: number = path[0].x,
            selfSymId: number = path[0].y,
            highlightPath: IMonsterWalk[] = [],
            pauseDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.2 / 2 : 0.2;

        const walkDurationNormal: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.15 / 2 : 0.15;
        let walkDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.15 / 2 : 0.15;
        const removeDurationNormal: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.1 / 2 : 0.1;
        let removeDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.1 / 2 : 0.1;

        let direction: string = (this.animation.scale.x > 0 ? "left" : "right"),
            payoutCollectCount: number = 0,
            isPlayTankDriveLoop: boolean = true;

        for (let i: number = 1; i < monsterPath.length; i++) {
            highlightPath.push(monsterPath[i]);
        }

        this._isAborted = false;

        // this.getAndUpdatePickDropFlagIdx(false, "bu");

        // Game.monsterCarriedSymbols[this._monsterIdx].splice(Game.monsterCarriedSymbols[this._monsterIdx].indexOf("BU"), 1);

        tl.add(() => {
            if (this.parent) {
                this.parent.addChild(this); // make sure that this is always on top from other monsters.

                // any monster on bottom, push it above this
                if (selfSymId + 1 < Game.ACTIVE_ROWS_NUM) {
                    const botReelsSymName: string = Game.model.gameData.evaluatedArea[selfReelId][selfSymId + 1];

                    if (Game.MONSTER_SYMBOLS.includes(botReelsSymName)) {
                        const botMonster: Symbol = Game.reelsView.reelModels[selfReelId].getSymbolAt(selfSymId + 1);

                        if (botMonster && botMonster.parent) {
                            botMonster.parent.addChild(botMonster);
                        }
                    }
                }
            }
        }, 0);

        tl.add(this.useWpRemoveFlag("bu"));
        tl.add(this.highlightMonsterPath(highlightPath));
        tl.add(() => {
            Game.soundPlayer.playSound(Sound.BU_DRILL_START, 1);
        });

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.BU_DRILL_LOOP, 1, Number.MAX_VALUE);
        }, ">0.294");

        // draw track at monster start position
        tl.add(() => {
            Game.symbolFloors.drawTrack(this, selfReelId, {reelId: path[0].x, rowId: path[0].y}, {
                reelId: -1,
                rowId: -1
            }, {reelId: path[1].x, rowId: path[1].y}, true);
            this.handleLoopStateWithMixDuration(0, MONSTER_WEAPON_ACTIONS.BU_LOOP, 0.2);
        });

        for (let i: number = 1; i < path.length; i++) {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[path[i].x][path[i].y],
                symbol: Symbol = Game.reelsView.reelModels[path[i].x].getSymbolAt(path[i].y, reelsSymName);

            // direction while walking
            direction = this.handleDirection(tl, direction, path, i, walkDuration, false, false);
            if (i > 7 && i <= 15) {
                walkDuration = walkDurationNormal / 1.2;
                removeDuration = removeDurationNormal / 1.2;
            } else if (i > 15 && i <= 22) {
                walkDuration = walkDurationNormal / 1.4;
                removeDuration = removeDurationNormal / 1.4;
            } else if (i > 22 && i <= 30) {
                walkDuration = walkDurationNormal / 1.6;
                removeDuration = removeDurationNormal / 1.6;
            } else if (i > 30) {
                walkDuration = walkDurationNormal / 2;
                removeDuration = removeDurationNormal / 2;
            }

            // #462
            if ((reelsSymName.includes("MX")) || (symbol.isRemoved) || (path[i].x === selfReelId && path[i].y === selfSymId)) {
                tl.to(this, {
                    x: (path[i].x * Game.SYMBOL_WIDTH),
                    y: (path[i].y * Game.SYMBOL_HEIGHT),
                    duration: removeDuration
                });
                tl.add(() => {
                    this.setLayer(path[i]);
                }, "<");
            } else {
                const symName: string = symbol.name;

                // #267
                if (!["GE", "XB", "MX", "MX_V_L", "MX_V_R"].includes(symName) && ((i !== (path.length - 1)) || (i === 1))) {
                    symbol.isRemoved = true;
                }

                if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symName)) {
                    tl.add(() => {
                        payoutCollectCount++;
                        this.playPayoutCollect(payoutCollectCount);
                    })
                }

                tl.addLabel("walk");

                if (isPlayTankDriveLoop) {
                    tl.add(() => {
                        Game.soundPlayer.playSound(Sound.TANK_DRIVE_LOOP, 1, Number.MAX_VALUE);
                    }, 0.27);
                    isPlayTankDriveLoop = false;
                }

                tl.add(() => {
                    Game.soundPlayer.playSound("xLootDrill" + this.name.substring(1), 1);
                }, "walk")

                tl.add(() => {
                    this.setLayer(path[i]);
                }, "walk");
                tl.to(this, {
                    x: (path[i].x * Game.SYMBOL_WIDTH),
                    y: (path[i].y * Game.SYMBOL_HEIGHT),
                    duration: walkDuration
                }, "walk");
                tl.addLabel("walkCompleted");

                tl.add(this.collectItems(symName, true, path[i], false), "walk");

                // #267
                if (!["GE", "XB", "MX", "MX_V_L", "MX_V_R"].includes(symName)) {
                    if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symName) && symName[1] !== this.name[1]) {
                        tl.add(() => {
                            // symbol name might get updated in between the timeline, hence re checking it again on symbol scope
                            if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symbol.name)) {
                                Game.overlaysForSymbols.playBzBreak(symbol);
                            }
                        }, "-=0.075");
                    }

                    tl.add(() => {
                        Game.reelsView.reelModels[path[i].x].removeSymbol(symbol);
                    }, "walkCompleted");

                    // #68
                    if (Game.MONSTER_SYMBOLS.includes(symName)) {
                        tl.add(Game.reelCorners.reduceLifeOfCorner(Game.MONSTER_NAME_TO_IDX[symName]), "walkCompleted");
                    }
                }

                if (!symName.includes("MX") && symName.charAt(0) === 'M') {
                    const multiplierVal: number = Number(symbol.aliasName.slice(1));

                    tl.add([
                        this.getStateAnimation(MONSTER_MOVE_ACTIONS.PICKUP_MULTIPLIER, 5),
                        Game.overlaysForSymbols.playMultiplierPick(symbol),
                        Game.multiplier.updateMultiplier(multiplierVal)
                    ], "walk");
                } else if (symName === "W") {
                    tl.add(() => {
                        Game.soundPlayer.playSound("xLootWild" + this.name.substring(1), 1);
                    });
                }
            }

            // #462
            if (!reelsSymName.includes("MX")) {
                tl.add(() => {
                    const isLastPathSym: boolean = i === path.length - 1;
                    const isFirstPathSym: boolean = i === 0;
                    const currPos: IFeatureData = {reelId: path[i].x, rowId: path[i].y},
                        prvPos: IFeatureData = {
                            reelId: isFirstPathSym ? -1 : path[i - 1].x,
                            rowId: isFirstPathSym ? -1 : path[i - 1].y
                        },
                        nxtPos: IFeatureData = {
                            reelId: isLastPathSym ? path[i - 1].x : path[i + 1].x,
                            rowId: isLastPathSym ? path[i - 1].y : path[i + 1].y
                        };
                    Game.symbolFloors.drawTrack(symbol, path[i].x, currPos, prvPos, nxtPos);
                });

                // remove highlight after visit
                tl.add(() => {
                    symbol.removeHighlight();
                }, ">");
            }
        }

        tl.add([
            () => {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[path[path.length - 1].x][path[path.length - 1].y],
                    lastSymbol: Symbol = Game.reelsView.reelModels[path[path.length - 1].x].getSymbolAt(path[path.length - 1].y, reelsSymName);

                Game.soundPlayer.stopSound(Sound.BU_DRILL_LOOP);
                Game.soundPlayer.playSound(Sound.BU_DRILL_LOOP_STOP, 1);
                this.handleSymbolsAfterWalk(selfReelId, selfSymId, path, true, lastSymbol.name);
                this.getStateAnimation(MONSTER_WEAPON_ACTIONS.BU_END, 5);
            }
        ]);
        tl.to(this, {
            duration: pauseDuration
        }, "<");

        return tl;
    }

    public walk(data: IMonster): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            path: IMonsterWalk[] = data.path,
            collectedIdx: number[] = this.getCollectedIdx(path),
            selfReelId: number = path[0].x,
            selfSymId: number = path[0].y,
            currMonster: IMonster = Game.model.gameData.monsters[this._monsterWalkIdx],
            isSingleMover: boolean = Game.isSingleMover(currMonster, path),
            pauseDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.2 / 2 : 0.2;

        const walkDurationNormal: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.15 / 2 : 0.15;
        let walkDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.15 / 2 : 0.15;
        const removeDurationNormal: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.1 / 2 : 0.1;
        let removeDuration: number = Game.apiPlugin.settings.get(APISetting.FAST_SPIN) ? 0.1 / 2 : 0.1;
        let hasPickedLastMultiplier: boolean = false;

        let lastMultiplierPath: IMonsterWalk[] = [],
            isMultiplierInSingleMover: boolean = false;

        if (isSingleMover) {
            lastMultiplierPath = this.formLastMultiplierPathData(path[path.length - 2], currMonster.lastMultiplierPosition);
            isMultiplierInSingleMover = this.isMultiplierPresentInSingleMover(path);
        }

        let amountCollectorCtr: number = 0,
            direction: string = (this.animation.scale.x > 0 ? "left" : "right"),
            payoutCollectCount: number = 0,
            isPlayTankDriveLoop: boolean = true;

        this._isAborted = false;

        tl.add(() => {
            if (this.parent) {
                this.parent.addChild(this); // make sure that this is always on top from other monsters.

                // any monster on bottom, push it above this
                if (selfSymId + 1 < Game.ACTIVE_ROWS_NUM) {
                    const botReelsSymName: string = Game.model.gameData.evaluatedArea[selfReelId][selfSymId + 1];

                    if (Game.MONSTER_SYMBOLS.includes(botReelsSymName)) {
                        const botMonster: Symbol = Game.reelsView.reelModels[selfReelId].getSymbolAt(selfSymId + 1);

                        if (botMonster && botMonster.parent) {
                            botMonster.parent.addChild(botMonster);
                        }
                    }
                }
                // any monster on right, push it above this
                if (selfReelId + 1 < Game.ACTIVE_REELS_NUM) {
                    const rightReelsSymName: string = Game.model.gameData.evaluatedArea[selfReelId + 1][selfSymId];

                    if (Game.MONSTER_SYMBOLS.includes(rightReelsSymName)) {
                        const rightMonster: Symbol = Game.reelsView.reelModels[selfReelId + 1].getSymbolAt(selfSymId);

                        if (rightMonster && rightMonster.parent) {
                            rightMonster.parent.addChild(rightMonster);
                        }
                    }
                }
            }

            this.handleLoopStateWithMixDuration(0, MONSTER_MOVE_ACTIONS.MOVING, 0.2);
        }, 0);

        if (path[0].canRemove) {
            // draw track at monster start position
            tl.add(() => {
                Game.symbolFloors.drawTrack(this, selfReelId, {reelId: path[0].x, rowId: path[0].y}, {
                    reelId: -1,
                    rowId: -1
                }, {reelId: path[1].x, rowId: path[1].y}, true);
            });
        }

        let amountCollectedSoFar: number = 0;

        // toss hat at first position if multiplier not present in single mover
        if (isSingleMover && !isMultiplierInSingleMover) {
            tl.add(Game.symbolFloors.playHatTossAnimation(path[0].x, path[0].y, this.name.toLowerCase(), data.lifeLeft));
            tl.add(() => {
                Game.clearPlayingTrack(this.animation, 0);
                this.getStateAnimationWithoutTL(MONSTER_MOVE_ACTIONS.MOVING_FAST, 0, true);
            });
        }

        for (let i: number = 1; i < path.length; i++) {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[path[i].x][path[i].y],
                symbol: Symbol = Game.reelsView.reelModels[path[i].x].getSymbolAt(path[i].y, reelsSymName);

            if (i > 7 && i <= 15) {
                walkDuration = walkDurationNormal / 1.2;
                removeDuration = removeDurationNormal / 1.2;
            } else if (i > 15 && i <= 22) {
                walkDuration = walkDurationNormal / 1.4;
                removeDuration = removeDurationNormal / 1.4;
            } else if (i > 22 && i <= 30) {
                walkDuration = walkDurationNormal / 1.6;
                removeDuration = removeDurationNormal / 1.6;
            } else if (i > 30) {
                walkDuration = walkDurationNormal / 2;
                removeDuration = removeDurationNormal / 2;
            }
            // direction while walking
            direction = this.handleDirection(tl, direction, path, i, walkDuration, false, isSingleMover && (hasPickedLastMultiplier || !isMultiplierInSingleMover));

            if (symbol.isRemoved || (path[i].x === selfReelId && path[i].y === selfSymId)) {
                tl.to(this, {
                    x: (path[i].x * Game.SYMBOL_WIDTH),
                    y: (path[i].y * Game.SYMBOL_HEIGHT),
                    duration: removeDuration
                });
                tl.add(() => {
                    this.setLayer(path[i]);
                }, "<");
            } else {
                const symName: string = symbol.name;

                if (i !== (path.length - 1) && path[i].canRemove) {
                    symbol.isRemoved = true;
                }

                tl.addLabel("walk");

                if (isPlayTankDriveLoop) {
                    tl.add(() => {
                        Game.soundPlayer.playSound(Sound.TANK_DRIVE_LOOP, 1, Number.MAX_VALUE);
                    }, 0.27);
                    isPlayTankDriveLoop = false;
                }

                if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symName)) {
                    tl.add(() => {
                        payoutCollectCount++;
                        this.playPayoutCollect(payoutCollectCount);
                    })
                }

                tl.add(() => {
                    this.setLayer(path[i]);
                }, "walk");
                tl.to(this, {
                    x: (path[i].x * Game.SYMBOL_WIDTH),
                    y: (path[i].y * Game.SYMBOL_HEIGHT),
                    duration: walkDuration,
                    ease: "none",
                }, "walk");

                tl.addLabel("walkCompleted");

                tl.add([
                    this.collectItems(symName, false, path[i], isSingleMover && (hasPickedLastMultiplier || !isMultiplierInSingleMover))
                ], "walk");


                if (isSingleMover && (hasPickedLastMultiplier || !isMultiplierInSingleMover)) {
                    tl.add(() => {
                        Game.clearPlayingTrack(this.animation, 0);
                        this.getStateAnimationWithoutTL(MONSTER_MOVE_ACTIONS.MOVING_FAST, 0, true);
                    });
                } else {
                    tl.add(() => {
                        Game.clearPlayingTrack(this.animation, 0);
                        this.getStateAnimationWithoutTL(MONSTER_MOVE_ACTIONS.MOVING, 0, true);
                    });
                }

                const idx: number = ((path[i].x * Game.TOTAL_REELS_NUM) + path[i].y);
                if (collectedIdx.includes(idx)) {
                    // #112
                    collectedIdx.splice(collectedIdx.indexOf(idx), 1);

                    tl.add(() => {
                        const amount: number = (data.collectWins[amountCollectorCtr] ? data.collectWins[amountCollectorCtr].multipliedAmount : 0);

                        if (symbol.isPayoutSymbol && amount) {
                            amountCollectorCtr++;
                            amountCollectedSoFar += amount;
                            this.addToAmount(amountCollectedSoFar);
                        }
                    }, "-=0.075");
                }

                if (path[i].canRemove) {
                    tl.add(() => {
                        Game.reelsView.reelModels[path[i].x].removeSymbol(symbol);
                    }, "walkCompleted");
                } else if (symName === "W") {
                    symbol.isDark = true; // #367
                    tl.add(symbol.getStateAnimation((symbol.isSplit ? WildStates.SPLIT_DARK : WildStates.DARK)), "walkCompleted");
                }

                if (!symName.includes("MX") && symName.charAt(0) === 'M') {
                    const multiplierVal: number = Number(symbol.aliasName.slice(1));

                    tl.add([
                        () => {
                            Game.clearPlayingTrack(this.animation, 5);
                        },
                        this.getStateAnimation(MONSTER_MOVE_ACTIONS.PICKUP_MULTIPLIER, 5),
                        Game.overlaysForSymbols.playMultiplierPick(symbol),
                        Game.multiplier.updateMultiplier(multiplierVal)
                    ], "walk");

                    if (isSingleMover && path[i].x === currMonster.lastMultiplierPosition.x && path[i].y === currMonster.lastMultiplierPosition.y) {
                        hasPickedLastMultiplier = true;
                        tl.add(Game.symbolFloors.playHatTossAnimation(path[i].x, path[i].y, this.name.toLowerCase(), data.lifeLeft));
                        tl.add(() => {
                            Game.clearPlayingTrack(this.animation, 0);
                            this.getStateAnimationWithoutTL(MONSTER_MOVE_ACTIONS.MOVING_FAST, 0, true);
                        });
                    }
                } else if (symName === "W") {
                    tl.add(() => {
                        Game.soundPlayer.playSound("xLootWild" + this.name.substring(1), 1);
                    });
                }
            }

            tl.add(() => {
                const isLastPathSym: boolean = i === path.length - 1;
                const isFirstPathSym: boolean = i === 0;
                const currPos: IFeatureData = {reelId: path[i].x, rowId: path[i].y},
                    prvPos: IFeatureData = {
                        reelId: isFirstPathSym ? -1 : path[i - 1].x,
                        rowId: isFirstPathSym ? -1 : path[i - 1].y
                    },
                    nxtPos: IFeatureData = {
                        reelId: isLastPathSym ? path[i - 1].x : path[i + 1].x,
                        rowId: isLastPathSym ? path[i - 1].y : path[i + 1].y
                    };
                Game.symbolFloors.drawTrack(symbol, path[i].x, currPos, prvPos, nxtPos);

                if (isSingleMover && (!symbol.name.includes("MX") && symbol.name.charAt(0) === 'M') && (path[i].x === currMonster.lastMultiplierPosition.x && path[i].y === currMonster.lastMultiplierPosition.y)) {
                    Game.symbolFloors.moveHatToTopOnFloor(path[i].x, path[i].y, data.lifeLeft);
                }
            });

            // remove highlight after visit
            tl.add(() => {
                symbol.removeHighlight();
            }, ">");
            if (isSingleMover && this.hasAllPayoutCollected(lastMultiplierPath) && i + 1 === path.length - 1 && path[i + 1].x === currMonster.lastMultiplierPosition.x && path[i + 1].y === currMonster.lastMultiplierPosition.y) {
                lastMultiplierPath.unshift({x: path[i].x, y: path[i].y, canRemove: true})
                tl.add(this.walkToLastMultiplerPos(lastMultiplierPath, walkDuration / 2, direction, true, data.lifeLeft));
                break;
            }
        }

        tl.add(() => {
            this.handleSymbolsAfterWalk(selfReelId, selfSymId, path, false, "");
        });
        tl.to(this, {
            duration: pauseDuration
        });

        return tl;
    }

    public hideAmount(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            playedBetValue: number = Game.model.gameData.playedBetValue,
            amount: number = +(this._amount.text),
            ratio: number = (amount / playedBetValue),
            hideDuration: number = 0.3;

        let animName: string = "";

        // #350
        if (ratio >= 1 && ratio <= 3) {
            animName = "portal1";
        } else if (ratio > 3 && ratio <= 5) {
            animName = "portal2";
        } else if (ratio > 5) {
            animName = "portal3";
        }

        if (animName && this._wins && this._wins.visible) {
            tl.add([
                () => {
                    this._wins.state.clearTracks();
                    this._wins.state.setEmptyAnimation(0, 0);
                    this._wins.skeleton.setToSetupPose();

                    if (Game.isPlayPickupHighlight) {
                        Game.soundPlayer.playSound(Sound.LOOT_PICKUP_HIGHLIGHT, 1);
                        Game.isPlayPickupHighlight = false;
                    }
                },
                Game.createTimelineForAnimation(this._wins, 0, animName)
            ]);
        }

        tl.to(this._amount, {
            duration: hideDuration,
            y: this._amount.y - 100
        }, "<");
        tl.to(this._amount, {
            duration: 0.1,
            alpha: 0,
            onComplete: () => {
                this._amount.text = "";
                this._amount.alpha = 1;
            }
        }, "-=0.1");

        tl.add(() => {
            this._wins.state.clearTracks();
            this._wins.visible = false;

            this._prevWinsAnimName = "";
        });

        return tl;
    }

    public sipBeer(scatterCount: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        let sipAnimStr: string = MONSTER_WEAPON_ACTIONS.S_USE;

        if (scatterCount > 1) {
            sipAnimStr += "_" + scatterCount;
        }

        tl.add([
            this.getStateAnimation(sipAnimStr, 5)
        ]);

        return tl;
    }

    public throwGrenade(grenadeTarget: IMonsterWalk, targetRowId: number, grSymbol: Symbol): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const range: string = this.getGrenadeTargetRange() || "mid";
        let throwDuration: number = 2.633; // duration for close range target
        this._grExecuteCount++;

        if (["mid", "far"].includes(range)) {
            throwDuration = 2.3;
        }

        // Game.monsterCarriedSymbols[this._monsterIdx].splice(Game.monsterCarriedSymbols[this._monsterIdx].indexOf("GR"), 1);

        tl.add([
            Game.showExplodePatternHighlighterOnSymbols(grenadeTarget.x - 1, targetRowId - 1, targetRowId + 2),
            Game.showExplodePatternHighlighterOnSymbols(grenadeTarget.x, targetRowId - 1, targetRowId + 2),
            Game.showExplodePatternHighlighterOnSymbols(grenadeTarget.x + 1, targetRowId - 1, targetRowId + 2),
            () => {
                Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1)
            }
        ], 0);
        throwDuration += (5 / 30);

        tl.add(this.useWpRemoveFlag("gr"));

        tl.add(this.handleGrenadeToss(grenadeTarget, range, targetRowId, grSymbol), throwDuration);

        if (Game.model.data.maxExpansion) {
            tl.add(Game.explodeSymbolsBeforeMaxExpansion(), "<0.5");
            tl.add(Game.expandGridForMaxExpansion(), "<0.55");
        }

        tl.add(Game.symbolFloors.stopExplodePatternHighlightAnimation());

        return tl;
    }

    public throwBazooka(target: IMonsterWalk): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // Game.monsterCarriedSymbols[this._monsterIdx].splice(Game.monsterCarriedSymbols[this._monsterIdx].indexOf("BZ"), 1);

        tl.add(this.useWpRemoveFlag("bz"));

        const bzCopy: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures("BZ/BZ")[0]),
            path: PIXI.IPointData[] = this.getPathData((Game.SYMBOL_WIDTH * target.x), (Game.SYMBOL_HEIGHT * target.y), this.x, this.y);

        bzCopy.anchor.set(0.5);
        bzCopy.scale.set(this.animation.scale.x, 1);
        bzCopy.position.set(this.x - 18, (this.y - Game.SYMBOL_HEIGHT / 2) + 60);
        bzCopy.rotation = 35.2;
        if (bzCopy.scale.x > 0) {
            bzCopy.x += 25;
            bzCopy.rotation = 33.9;
        }

        tl.add(() => {
            Game.overlaysForSymbols.addChild(bzCopy);
        }, 57 / 30);
        tl.to(bzCopy, {
            x: bzCopy.x + (bzCopy.scale.x > 0 ? 5 : -5),
            duration: 2 / 30
        });
        tl.to(bzCopy, {
            ease: "power2.out",
            motionPath:
                {
                    path: path,
                    type: "cubic",
                    autoRotate: true,
                    useRadians: true,
                    curviness: 3,
                    alignOrigin: [0.5, 0.5]
                },
            transformOrigin: "50% 50%",
            duration: 9 / 30
        }, 59 / 30);

        tl.to(bzCopy.scale, {
            duration: 0.2,
            x: 0,
            y: 0,
            onComplete: () => {
                bzCopy.parent.removeChild(bzCopy);
            }
        }, 65 / 30);

        return tl;
    }

    public playAirstrikeFly(airstrikes: IMonsterWalk[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const randomASFly: number = MathHelper.randomIntInRange(1, 6);

        tl.add([
            Game.createTimelineForAnimation(this._airstrikeAnimation, 9, "airstrike_" + randomASFly, false),
            () => {
                Game.soundPlayer.playSound(Sound.ATTACK_HIGHLIGHT, 1)
            }
        ], 0);
        airstrikes.forEach((airstrike: IMonsterWalk) => {
            tl.add(Game.showExplodePatternHighlighterOnSymbols(airstrike.x, 0, Game.ACTIVE_ROWS_NUM), 0);
            for (let reelId: number = 0; reelId < Game.ACTIVE_REELS_NUM; reelId++) {
                tl.add(Game.showExplodePatternHighlighterOnSymbols(reelId, airstrike.y, airstrike.y + 1), 0);
            }
        });

        return tl;
    }

    public throwAirstrike(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        // Game.monsterCarriedSymbols[this._monsterIdx].splice(Game.monsterCarriedSymbols[this._monsterIdx].indexOf("AS"), 1);

        tl.add(this.useWpRemoveFlag("as"));

        tl.add(Game.symbolFloors.stopExplodePatternHighlightAnimation());

        return tl;
    }

    public getDistinctMPath(arr: IMonsterWalk[]): IMonsterWalk[] {
        return arr.filter((a, i) => arr.findIndex((s) => a.x === s.x && a.y == s.y) === i);
    }

    public useWpRemoveFlag(carriedItem: string): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            removedIdx: number = this.getAndUpdatePickDropFlagIdx(false, carriedItem, tl),
            wpUseSoundState: string = carriedItem.toUpperCase() + "_ACTIVATE",
            flagRemState: string = "flags/flag_" + removedIdx + "_" + carriedItem + "_reset";
        let wpUseState: string = carriedItem + "/use_" + carriedItem,
            durationIdxForActivateSound: number = 0;

        if (carriedItem === "gr") {
            const grRange: string = this.getGrenadeTargetRange();
            wpUseState += grRange ? "_" + this.getGrenadeTargetRange() : "";
        }

        tl.add([
            () => {
                if (carriedItem !== "bu") {
                    if (carriedItem === "gr") {
                        const isTriggerAttackGR: number = MathHelper.randomIntInRange(0, 2); // random 2/3
                        if (isTriggerAttackGR) {
                            tl.add(() => {
                                Game.soundPlayer.playSound(this.name + "AttackGrenade", 1);
                            }, 0.28)
                        } else {
                            tl.add(() => {
                                Game.soundPlayer.playSound(this.name + "Attack" + MathHelper.randomIntInRange(1, 3), 1);
                            }, 0.28)
                        }
                    } else if (carriedItem === "bz") {
                        const isTriggerAttackBZ: number = MathHelper.randomIntInRange(0, 2); // random 2/3
                        if (isTriggerAttackBZ) {
                            tl.add(() => {
                                Game.soundPlayer.playSound(this.name + "AttackRocket", 1);
                            }, 0.28)
                        } else {
                            tl.add(() => {
                                Game.soundPlayer.playSound(this.name + "Attack" + MathHelper.randomIntInRange(1, 3), 1);
                            }, 0.28)
                        }
                    } else {
                        tl.add(() => {
                            Game.soundPlayer.playSound(this.name + "Attack" + MathHelper.randomIntInRange(1, 3), 1);
                        }, 0.28)
                    }
                }

                Game.soundPlayer.playSound((Sound as any)[wpUseSoundState], 1);

                if (carriedItem === "as") {
                    Game.soundPlayer.playSound("attackAnticipation", 1);
                }

                if (Game.getCurrentGameMode() === GameMode.NORMAL) {
                    if (carriedItem === "as") {
                        Game.soundPlayer.fadeInAndStartSound({
                            name: Sound.AS_SNARE,
                            from: 0,
                            to: 1,
                            duration: 1500,
                            isResume: true,
                            repeat: Number.MAX_VALUE
                        });
                    } else if (["gr"].includes(carriedItem)) {
                        Game.soundPlayer.fadeInAndStartSound({
                            name: Sound.SNARE_PLAYER,
                            from: 0,
                            to: 1,
                            duration: 1500,
                            isResume: true,
                            repeat: Number.MAX_VALUE
                        });
                    }
                }
            },
            this.getStateAnimation(wpUseState, 5),
            this.getStateAnimation(flagRemState, removedIdx)
        ]);

        if (carriedItem === "as") {
            tl.add(() => {
                Game.soundPlayer.playSound(this.name + "AirstrikeGoodbye" + MathHelper.randomIntInRange(1, 3), 1);
            });
        }

        if (carriedItem === "gr") {
            durationIdxForActivateSound = 2;
        } else if (carriedItem === "bz") {
            durationIdxForActivateSound = 1.7;
        } else if (carriedItem === "as") {
            durationIdxForActivateSound = 1.4;
        }

        if (durationIdxForActivateSound > 0) {
            tl.add(() => {
                Game.soundPlayer.playSound(this.name + "Activate" + MathHelper.randomIntInRange(1, 3), 1);
            }, durationIdxForActivateSound);
        }

        return tl;
    }

    public getStateAnimation(state: string, trackIndex: number, loop: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this.animation && this.animation.state.hasAnimation(state)) {
            const animTime: number = this.getAnimTime(state);

            tl.add(() => {
                if (trackIndex === 5) {
                    this._stateAnim = this.animation.state.setAnimation(trackIndex, state, loop);
                } else {
                    this.animation.state.setAnimation(trackIndex, state, loop);
                }
            });
            tl.to(this, {
                duration: animTime
            });
        }

        return tl;
    }

    public addWeaponFlag(carriedItem: string, carriedIndex?: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            carriedIdx: number = carriedIndex ? carriedIndex : this.getAndUpdatePickDropFlagIdx(true, carriedItem, tl),
            animName: string = "flags/flag_" + carriedIdx + "_" + carriedItem,
            animTime: number = this.getAnimTime(animName);

        tl.add(() => {
            this.getStateAnimationWithoutTL(animName, carriedIdx);
        });
        tl.to(this, {
            duration: animTime
        });
        tl.add(() => {
            this.getStateAnimationWithoutTL(animName + "_loop", carriedIdx, true);
        });

        return tl;
    }

    public handleDirection(tl: gsap.core.Timeline, direction: string, path: IMonsterWalk[], i: number, duration: number, isBUDriving: boolean, isNoHat: boolean): string {
        tl.addLabel("turn");

        if ((direction === "left") && (i - 1 >= 0 && path[i].x > path[i - 1].x)) {
            direction = "right";
            this._isFacingLeft = false;
            tl.add([
                () => {
                    Game.soundPlayer.playSound(Sound.TANK_U_TURN, 1);

                    this._extraFlagsText.x = -70;
                },
                this.getStateAnimation(MONSTER_MOVE_ACTIONS.TURN + (isNoHat ? "_no_hat" : ""), 0)
            ], "turn");

            tl.to(this.animation.scale, {
                x: -1,
                duration,
            }, "turn");

            tl.add(() => {
                Game.clearPlayingTrack(this.animation, 0);
                this.getStateAnimationWithoutTL(isBUDriving ? MONSTER_WEAPON_ACTIONS.BU_LOOP : (isNoHat ? MONSTER_MOVE_ACTIONS.MOVING_FAST : MONSTER_MOVE_ACTIONS.MOVING), 0, true);
            });
        } else if ((direction === "right") && (i - 1 >= 0 && path[i].x < path[i - 1].x)) {
            direction = "left";
            this._isFacingLeft = true;
            tl.add([
                () => {
                    Game.soundPlayer.playSound(Sound.TANK_U_TURN, 1);

                    this._extraFlagsText.x = 70;
                },
                this.getStateAnimation(MONSTER_MOVE_ACTIONS.TURN + (isNoHat ? "_no_hat" : ""), 0)
            ], "turn");

            tl.to(this.animation.scale, {
                x: 1,
                duration,
            }, "turn");

            tl.add(() => {
                Game.clearPlayingTrack(this.animation, 0);
                this.getStateAnimationWithoutTL(isBUDriving ? MONSTER_WEAPON_ACTIONS.BU_LOOP : (isNoHat ? MONSTER_MOVE_ACTIONS.MOVING_FAST : MONSTER_MOVE_ACTIONS.MOVING), 0, true);
            });
        }

        return direction;
    }

    public skip(): void {
        if (this._stateAnim && this._stateAnim.animation) {
            this._stateAnim.trackTime = this._stateAnim.animation.duration;

            this._stateAnim = undefined;
        }
    }

    private getAndUpdatePickDropFlagIdx(isPick: boolean, carriedItem: string, tl: gsap.core.Timeline): number {
        const wpArr: string[] = Game.monsterCarriedSymbols[this._monsterIdx];
        let pickDropFlagStr: string = "";

        if (isPick) {
            if (wpArr.length > 0) {
                let nextFlagIdx: number = +wpArr[wpArr.length - 1].split("_")[1] + 1;

                if (nextFlagIdx > 4 || wpArr.join("").includes(nextFlagIdx + "")) {
                    for (let fgIdx = 1; fgIdx <= 4; fgIdx++) {
                        if (!wpArr.join("").includes(fgIdx + "")) {
                            pickDropFlagStr = carriedItem + "_" + fgIdx;
                            break;
                        }
                    }
                } else {
                    pickDropFlagStr = carriedItem + "_" + nextFlagIdx;
                }
            } else {
                pickDropFlagStr = carriedItem + "_" + (wpArr.length + 1);
            }
            if (pickDropFlagStr) {
                wpArr.push(pickDropFlagStr);
            } else {
                // #467
                tl.add(() => {
                    this._extraFlags.push(carriedItem);
                    this._extraFlagsNum++;
                    this.setExtraFlagsText();

                    Game.monsterExtraCarriedSymbols[this._monsterIdx] = this._extraFlags;
                }, 0);
            }
        } else {
            let itemFound: boolean = false;
            for (let i: number = 0; i < wpArr.length; i++) {
                if (wpArr[i].includes(carriedItem)) {
                    pickDropFlagStr = wpArr.splice(i, 1)[0];

                    // #467
                    itemFound = true;
                    if (this._extraFlags.length && wpArr.length < 4) {
                        const nextItem: string | undefined = this._extraFlags.shift();
                        if (nextItem) {
                            const flagIdx: number = +(pickDropFlagStr.split("_")[1]);
                            wpArr.push(nextItem + "_" + flagIdx);

                            tl.eventCallback("onComplete", () => {
                                const animName: string = "flags/flag_" + (flagIdx + "_" + nextItem),
                                    timeline: gsap.core.Timeline = gsap.timeline(),
                                    animTime: number = this.getAnimTime(animName);

                                timeline.add(() => {
                                    this.getStateAnimationWithoutTL(animName, flagIdx);
                                });
                                timeline.to(this, {
                                    duration: animTime
                                });
                                timeline.add(() => {
                                    this.getStateAnimationWithoutTL(animName + "_loop", flagIdx, true);
                                });

                                this._extraFlagsNum--;
                                this.setExtraFlagsText();
                            });
                        }

                        Game.monsterExtraCarriedSymbols[this._monsterIdx] = this._extraFlags;
                    }
                    break;
                }
            }
            // #467
            if (!itemFound) {
                const carriedItemIdxInExtraFlags: number = this._extraFlags.indexOf(carriedItem);
                if (carriedItemIdxInExtraFlags > -1) {
                    this._extraFlags.splice(carriedItemIdxInExtraFlags, 1);
                    Game.monsterExtraCarriedSymbols[this._monsterIdx] = this._extraFlags;
                    tl.eventCallback("onComplete", () => {
                        this._extraFlagsNum--;
                        this.setExtraFlagsText();
                    });
                }
            }
        }

        return +pickDropFlagStr.split("_")[1];
    }

    private isMultiplierPresentInSingleMover(path: IMonsterWalk[]): boolean {
        for (let i: number = 1; i < path.length; i++) {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[path[i].x][path[i].y],
                symbol: Symbol = Game.reelsView.reelModels[path[i].x].getSymbolAt(path[i].y, reelsSymName);

            if (!symbol.name.includes("MX") && symbol.name.charAt(0) === 'M') {
                return true;
            }
        }

        return false;
    }

    private walkToLastMultiplerPos(path: IMonsterWalk[], walkDuration: number, direction: string, isPickHat: boolean, monsterLife: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        for (let i = 1; i < path.length; i++) {
            direction = this.handleDirection(tl, direction, path, i, walkDuration, false, true);

            tl.to(this, {
                x: (path[i].x * Game.SYMBOL_WIDTH),
                y: (path[i].y * Game.SYMBOL_HEIGHT),
                duration: walkDuration
            });

            if (i === path.length - 1 && isPickHat) {
                tl.add(Game.symbolFloors.hideHat(monsterLife));
                tl.add(this.getStateAnimation("pickup_hat", 5));
            }
        }


        return tl;
    }

    private formLastMultiplierPathData(sourcePos: IMonsterWalk, targetPos: IPosition): IMonsterWalk[] {
        const backtrackPath: IMonsterWalk[] = [];
        let {x: sourceX, y: sourceY} = sourcePos;
        let {x: targetX, y: targetY} = targetPos;

        while ((sourceX >= 0 && sourceX < Game.ACTIVE_REELS_NUM && sourceY >= 0 && sourceY < Game.ACTIVE_ROWS_NUM) && !(sourceX === targetX && sourceY === targetY)) {
            if (sourceX < targetX && !this.isCornerPosition(sourceX + 1, sourceY)) {
                sourceX++;
            } else if (sourceX > targetX && !this.isCornerPosition(sourceX - 1, sourceY)) {
                sourceX--;
            } else if (sourceY < targetY && !this.isCornerPosition(sourceX, sourceY + 1)) {
                sourceY++;
            } else if (sourceY > targetY && !this.isCornerPosition(sourceX, sourceY - 1)) {
                sourceY--;
            }

            backtrackPath.push({x: sourceX, y: sourceY, canRemove: true});
        }

        return backtrackPath;
    }

    private isCornerPosition(x: number, y: number): boolean {
        if ((x === 0 && y === 0) || (x === 0 && y === Game.ACTIVE_ROWS_NUM - 1) || (x === Game.ACTIVE_REELS_NUM - 1 && y === 0) || (x === Game.ACTIVE_REELS_NUM - 1 && y === Game.ACTIVE_ROWS_NUM - 1)) {
            return true;
        }
        return false;
    }

    private hasAllPayoutCollected(path: IMonsterWalk[]): boolean {
        return path.every((monsterPath: IMonsterWalk) => {
            const reelsSymName: string = Game.model.gameData.evaluatedArea[monsterPath.x][monsterPath.y],
                symbol: Symbol = Game.reelsView.reelModels[monsterPath.x].getSymbolAt(monsterPath.y, reelsSymName);

            if (!Game.MONSTER_SYMBOLS.concat(["GE", "XB"]).includes(symbol.name)) {
                return symbol.isRemoved;
            } else {
                return true;
            }
        })
    }

    private flagsInit(): void {
        const prevCarriedSymbols: string[] = Game.monsterCarriedSymbols[this._monsterIdx];
        const wpLen: number = prevCarriedSymbols.length;
        if (wpLen > 0) {
            for (let ctr: number = 0; ctr < Math.min(4, wpLen); ctr++) {
                const weapon: string[] = prevCarriedSymbols[ctr].split("_");
                this.addWeaponFlag(weapon[0].toLowerCase(), +weapon[1]).totalProgress(1);
            }

            // #467
            if (wpLen > 4) {
                for (let i: number = 4; i < wpLen; i++) {
                    this._extraFlags.push(prevCarriedSymbols[i].split("_")[0]);
                }
                while (prevCarriedSymbols.length !== 4) {
                    prevCarriedSymbols.pop();
                }
                this._extraFlagsNum = this._extraFlags.length + 4;
                this._extraFlagsText.text = this._extraFlagsNum + "";
            } else if (this._extraFlags.length) {
                this._extraFlagsNum = this._extraFlags.length + 4;
                this._extraFlagsText.text = this._extraFlagsNum + "";
            }
        }

        this._airstrikeAnimation = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("airstrike"));

        this.addChild(this._airstrikeAnimation);
    }

    // #467
    private setExtraFlagsText(): void {
        if (this._extraFlagsNum < 5) {
            this._extraFlagsText.text = "";
        } else {
            gsap.killTweensOf(this._extraFlagsText);
            gsap.to(this._extraFlagsText.scale, {
                duration: 0.2,
                x: 1.5,
                y: 1.5,
                onComplete: () => {
                    this._extraFlagsText.text = this._extraFlagsNum + "";
                    gsap.to(this._extraFlagsText.scale, {
                        duration: 0.1,
                        x: 1,
                        y: 1
                    });
                }
            });
        }
    }

    // #30
    private setLayer(nextPos: IPosition): void {
        const nextReelId: number = nextPos.x,
            nextReelPos: number = nextPos.y;

        const nextLeftReel: number = nextReelId - 1;
        let nextLeftMonster: Symbol | undefined;
        if (nextLeftReel > -1) {
            let nextReelsSymName: string = Game.model.gameData.evaluatedArea[nextLeftReel][nextReelPos];
            if ((Game.BOOSTER_SYMBOLS.includes(nextReelsSymName)) || (nextReelsSymName === "S") || (nextReelsSymName !== "MX" && nextReelsSymName.charAt(0) === "M")) {
                nextLeftMonster = Game.reelsView.reelModels[nextLeftReel].getSymbolAt(nextReelPos);
                nextReelsSymName = nextLeftMonster?.name;
            }
            if (nextReelsSymName && Game.MONSTER_SYMBOLS.includes(nextReelsSymName)) {
                if (!nextLeftMonster) {
                    nextLeftMonster = Game.reelsView.reelModels[nextLeftReel].getSymbolAt(nextReelPos, nextReelsSymName);
                }
                const idxOfLeftMonster: number = Game.monsterReelsView.monsterSymbolContainer.children.indexOf(nextLeftMonster);
                if (this.parent && nextReelsSymName !== this.name && idxOfLeftMonster > -1) {
                    this.parent.addChildAt(this, idxOfLeftMonster + 1);
                }
            }
        }

        const nextRightReel: number = nextReelId + 1;
        let nextRightMonster: Symbol | undefined;
        if (nextRightReel < Game.ACTIVE_REELS_NUM) {
            let nextReelsSymName: string = Game.model.gameData.evaluatedArea[nextRightReel][nextReelPos];
            if ((Game.BOOSTER_SYMBOLS.includes(nextReelsSymName)) || (nextReelsSymName === "S") || (nextReelsSymName !== "MX" && nextReelsSymName.charAt(0) === "M")) {
                nextRightMonster = Game.reelsView.reelModels[nextRightReel].getSymbolAt(nextReelPos);
                nextReelsSymName = nextRightMonster?.name;
            }
            if (Game.MONSTER_SYMBOLS.includes(nextReelsSymName)) {
                if (!nextRightMonster) {
                    nextRightMonster = Game.reelsView.reelModels[nextRightReel].getSymbolAt(nextReelPos, nextReelsSymName);
                }
                const idxOfCurrentMonster: number = Game.monsterReelsView.monsterSymbolContainer.children.indexOf(this);
                if (nextRightMonster?.parent && nextReelsSymName !== this.name) {
                    nextRightMonster.parent.addChildAt(nextRightMonster, idxOfCurrentMonster + 1);
                }
            }
        }

        const nextUpperPos: number = nextReelPos - 1;
        let nextUpperMonster: Symbol | undefined;
        if (nextUpperPos > -1) {
            let nextReelsSymName: string = Game.model.gameData.evaluatedArea[nextReelId][nextUpperPos];
            if ((nextReelsSymName && Game.BOOSTER_SYMBOLS.includes(nextReelsSymName)) || (nextReelsSymName === "S") || (nextReelsSymName !== "MX" && nextReelsSymName.charAt(0) === "M")) {
                nextUpperMonster = Game.reelsView.reelModels[nextReelId].getSymbolAt(nextUpperPos);
                nextReelsSymName = nextUpperMonster?.name;
            }
            if (nextReelsSymName && Game.MONSTER_SYMBOLS.includes(nextReelsSymName)) {
                if (!nextUpperMonster) {
                    nextUpperMonster = Game.reelsView.reelModels[nextReelId].getSymbolAt(nextUpperPos, nextReelsSymName);
                }
                const idxOfUpperMonster: number = Game.monsterReelsView.monsterSymbolContainer.children.indexOf(nextUpperMonster);
                if (this.parent && nextReelsSymName !== this.name && idxOfUpperMonster > -1) {
                    this.parent.addChildAt(this, idxOfUpperMonster + 1);
                }
            }
        }

        const nextBotPos: number = nextReelPos + 1;
        let nextBotMonster: Symbol | undefined;
        if (nextBotPos < Game.ACTIVE_ROWS_NUM) {
            let nextReelsSymName: string = Game.model.gameData.evaluatedArea[nextReelId][nextBotPos];
            if ((nextReelsSymName && Game.BOOSTER_SYMBOLS.includes(nextReelsSymName)) || (nextReelsSymName === "S") || (nextReelsSymName !== "MX" && nextReelsSymName.charAt(0) === "M")) {
                nextBotMonster = Game.reelsView.reelModels[nextReelId].getSymbolAt(nextBotPos);
                nextReelsSymName = nextBotMonster?.name;
            }
            if (nextReelsSymName && Game.MONSTER_SYMBOLS.includes(nextReelsSymName)) {
                if (!nextBotMonster) {
                    nextBotMonster = Game.reelsView.reelModels[nextReelId].getSymbolAt(nextBotPos, nextReelsSymName);
                }
                const idxOfCurrentMonster: number = Game.monsterReelsView.monsterSymbolContainer.children.indexOf(this);
                // #400
                if (nextBotMonster?.parent && nextReelsSymName !== this.name && idxOfCurrentMonster > -1) {
                    nextBotMonster.parent.addChildAt(nextBotMonster, idxOfCurrentMonster + 1);
                }
            }
        }
    }

    private collectItems(symName: string, isBUDriving: boolean, currPos: IMonsterWalk | undefined, isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (symName === "S") {
            tl.add(this.collectScatter(isBUDriving, currPos, isNoHat));
        } else if (Game.BOOSTER_SYMBOLS.includes(symName)) {
            tl.add(() => {
                // #422
                Game.clearPlayingTrack(this.animation, 0);
                Game.clearPlayingTrack(this.animation, 5);
                this.getStateAnimationWithoutTL(SymbolStates.NORMAL, 5, true);
            });

            if (symName === "BU") {
                tl.add(this.collectBulldozer(isNoHat));
            } else if (symName === "GR") {
                tl.add(this.collectGrenade(isNoHat));
            } else if (symName === "BZ") {
                tl.add(this.collectBazooka(isNoHat));
            } else if (symName === "AS") {
                tl.add(this.collectAirstrike(isNoHat));
            }

            tl.add([
                this.addWeaponFlag(symName.toLowerCase()),
                this.checkNeighboursWhileCollecting(currPos),
                () => {
                    Game.soundPlayer.playSound(this.name + "BoosterPickup" + MathHelper.randomIntInRange(1, 3), 1);
                }
            ], "<");
            tl.add(() => {
                Game.clearPlayingTrack(this.animation, 5);
                Game.clearPlayingTrack(this.animation, 0);
                this.getStateAnimationWithoutTL(isBUDriving ? MONSTER_WEAPON_ACTIONS.BU_LOOP : (isNoHat ? MONSTER_MOVE_ACTIONS.MOVING_FAST : MONSTER_MOVE_ACTIONS.MOVING), 0, true);
            });
        }

        return tl;
    }

    private getCollectedIdx(path: IMonsterWalk[]): number[] {
        const collectedIdx: number[] = [];

        for (let i: number = 0; i < path.length; i++) {
            const idx: number = ((path[i].x * Game.TOTAL_REELS_NUM) + path[i].y);

            if (!collectedIdx.includes(idx)) {
                collectedIdx.push(idx);
            }
        }

        return collectedIdx;
    }

    private playPayoutCollect(collectCount: number): void {
        const soundKey: string = "LOOT_" + Game.MONSTER_NAME_TO_COLOR[this.name] + ((collectCount % 4) + 1);
        Game.soundPlayer.playSound((Sound as any)[soundKey], 1);
    }

    private collectScatter(isBUDriving: boolean, currPos: IMonsterWalk | undefined, isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        let scatterPickStr: string = "";
        let scatterPickTl: gsap.core.Timeline;
        const scatterCount: number = Game.scatterInventory.count + 1;

        if (isNoHat && Game.model.data.mode.includes(GameModeNames.FREESPIN)) {
            if (!this._isFacingLeft) {
                scatterPickStr += "_plus_1";
            } else {
                scatterPickStr += "_plus";
            }
        } else if (Game.model.data.mode.includes(GameModeNames.FREESPIN)) {
            scatterPickStr += "_plus_1";
        } else {
            if (scatterCount === 3) {
                scatterPickStr = "_" + scatterCount + "d";
            } else if (scatterCount === 4) {
                scatterPickStr = "_" + scatterCount + "th";
            }
        }

        if (!this._isFacingLeft) {
            scatterPickStr += "_mirrored";
        }

        tl.add([
            () => {
                Game.soundPlayer.playSound("scatterPickup" + scatterCount, 1);

                if (!this._isAborted) {
                    // #422
                    Game.clearPlayingTrack(this.animation, 0);
                    Game.clearPlayingTrack(this.animation, 5);
                    this.getStateAnimationWithoutTL(SymbolStates.NORMAL, 5, true);

                    scatterPickTl = this.getStateAnimationTL(MONSTER_WEAPON_ACTIONS.S_PICKUP + scatterPickStr + (isNoHat ? "_no_hat" : ""), 5);
                }
            },
            this.checkNeighboursWhileCollecting(currPos)
        ]);
        tl.to(this, {
            duration: this.getAnimTime(MONSTER_WEAPON_ACTIONS.S_PICKUP + scatterPickStr + (isNoHat ? "_no_hat" : "")) // the duration is same even if the character is mirrored
        }, "<");

        tl.add(Game.scatterInventory.updateScatter(), "<0.5");

        tl.add(() => {
            scatterPickTl?.progress(1);
            Game.clearPlayingTrack(this.animation, 5);
            Game.clearPlayingTrack(this.animation, 0);
            this.getStateAnimationWithoutTL(isBUDriving ? MONSTER_WEAPON_ACTIONS.BU_LOOP : (isNoHat ? MONSTER_MOVE_ACTIONS.MOVING_FAST : MONSTER_MOVE_ACTIONS.MOVING), 0, true);
        });

        return tl;
    }

    private collectBulldozer(isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animName: string = MONSTER_WEAPON_ACTIONS.BU_PICK + (isNoHat ? "_no_hat" : ""),
            animTime: number = this.getAnimTime(animName);

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.BU_PICK, 1);

            this.getStateAnimationWithoutTL(animName, 5);
        });
        tl.to(this, {
            duration: animTime
        });

        // Game.monsterCarriedSymbols[this._monsterIdx].push("BU");

        return tl;
    }

    private collectGrenade(isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animName: string = MONSTER_WEAPON_ACTIONS.GR_PICK + (isNoHat ? "_no_hat" : ""),
            animTime: number = this.getAnimTime(animName);

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.GR_PICK, 1);

            this.getStateAnimationWithoutTL(animName, 5);
        });
        tl.to(this, {
            duration: animTime
        });

        // Game.monsterCarriedSymbols[this._monsterIdx].push("GR");

        return tl;
    }

    private collectBazooka(isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animName: string = MONSTER_WEAPON_ACTIONS.BZ_PICK + (isNoHat ? "_no_hat" : ""),
            animTime: number = this.getAnimTime(animName);

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.BZ_PICK, 1);

            this.getStateAnimationWithoutTL(animName, 5);
        });
        tl.to(this, {
            duration: animTime
        });

        // Game.monsterCarriedSymbols[this._monsterIdx].push("BZ");

        return tl;
    }

    private collectAirstrike(isNoHat: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            animName: string = MONSTER_WEAPON_ACTIONS.AS_PICK + (isNoHat ? "_no_hat" : ""),
            animTime: number = this.getAnimTime(animName);

        tl.add(() => {
            Game.soundPlayer.playSound(Sound.AS_PICK, 1);

            this.getStateAnimationWithoutTL(animName, 5);
        });
        tl.to(this, {
            duration: animTime
        });

        // Game.monsterCarriedSymbols[this._monsterIdx].push("AS");

        return tl;
    }

    private handleGrenadeToss(grenadeTarget: IMonsterWalk, range: string, targetRowId: number, grSymbol: Symbol): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            grCopy: PIXI.Sprite = new PIXI.Sprite(Game.assetLoader.getTextures("GR/gr_parts/granade_cooked")[0]),
            reelsSymName = Game.model.gameData.evaluatedArea[grenadeTarget.x][grenadeTarget.y],
            attackedSymbol: Symbol = Game.reelsView.reelModels[grenadeTarget.x].getSymbolAt(grenadeTarget.y, reelsSymName);

        grCopy.anchor.set(0.5, 0.5);
        grCopy.scale.set(Game.scaleFactor);

        if (range === "close") {
            grCopy.angle = 0;
            grCopy.position.set(0, -100);
        } else if (range === "mid") {
            grCopy.angle = 45;
            grCopy.position.set(0, 0);
        } else if (range === "far") {
            grCopy.angle = 90;
            grCopy.position.set(0, -100);
        }

        tl.add(() => {
            this.addChild(grCopy);
            grCopy.position.set(this.x + grCopy.x, this.y + grCopy.y);
            Game.overlaysForSymbols.addChild(grCopy);
        });

        tl.to(grCopy, {
            duration: 0.3,
            x: (grenadeTarget.x * Game.SYMBOL_WIDTH),
            y: ((grenadeTarget.y * Game.SYMBOL_HEIGHT) - (Game.SYMBOL_HEIGHT / 2))
        });

        const explosionTlIdx: number = Game.model.data.maxExpansion ? 0.3 : tl.duration(),
            explosionMarkTlIdx: string = Game.model.data.maxExpansion ? "<0.1" : "<0.2";

        tl.addLabel("explosion", explosionTlIdx);
        tl.add(attackedSymbol.playBigExplode(), "explosion");
        tl.add([
            () => {
                Game.soundPlayer.playSound(Sound.GR_EXPLODE, 1);
            },
            Game.shakeScreen()
        ], "explosion")

        tl.add(() => {
            if (Game.getCurrentGameMode() === GameMode.NORMAL) {
                Game.soundPlayer.fadeInAndStartSound({
                    name: Sound.SNARE_PLAYER,
                    from: 1,
                    to: 0,
                    duration: 2000,
                    repeat: Number.MAX_VALUE,
                    isResume: true
                });
            }
        }, "<");

        tl.add([
            Game.explodeSymbols(grenadeTarget.x - 1, targetRowId - 1, targetRowId + 2, true, false),
            Game.explodeSymbols(grenadeTarget.x, targetRowId - 1, targetRowId + 2, true, false),
            Game.explodeSymbols(grenadeTarget.x + 1, targetRowId - 1, targetRowId + 2, true, false),
        ], "explosion");

        tl.add(Game.symbolFloors.showExplosionMark(grenadeTarget.x, grenadeTarget.y), explosionMarkTlIdx);

        if (Game.canRemoveSymbol(grSymbol)) {
            tl.add(Game.reelsView.reelModels[grenadeTarget.x].removeSymbol(grSymbol), "<");
            tl.add(() => {
                grSymbol.isRemoved = true;
            });
        }
        tl.to(grCopy.scale, {
            duration: 0.15,
            x: 0,
            y: 0
        }, "explosion");
        tl.add(() => {
            grCopy.parent.removeChild(grCopy);
        });

        return tl;
    }

    private addToAmount(amountCollectedSoFar: number): void {
        const playedBetValue: number = Game.model.gameData.playedBetValue,
            ratio: number = (amountCollectedSoFar / playedBetValue),
            tl: gsap.core.Timeline = gsap.timeline();
        let animName: string = "";

        // #350
        if (ratio >= 1 && ratio <= 3) {
            animName = "shine1";
        } else if (ratio > 3 && ratio <= 5) {
            animName = "shine2";
        } else if (ratio > 5) {
            animName = "shine3";
        }

        this._amount.fontSize = 14;
        this._amount.text = (amountCollectedSoFar).toFixed(2);
        while (this._amount.textWidth > 130) {
            this._amount.fontSize--;
        }

        this._amount.y = (Game.SYMBOL_HEIGHT / 2) - (this._amount.height / 2);

        // #350
        if (this._prevWinsAnimName !== animName && animName && this._wins) {
            this._prevWinsAnimName = animName;
            tl.add(() => {
                this._wins.y = this._amount.y + 10;
                this._wins.visible = true;
                this._wins.state.setAnimation(0, animName, true);
            });
        }
    }

    private handleSymbolsAfterWalk(selfReelId: number, selfSymId: number, path: IMonsterWalk[], fromBU: boolean, lastSymName: string): void {
        Game.clearPlayingTrack(this.animation, 0, 0.2);

        Game.soundPlayer.stopSound(Sound.TANK_DRIVE_LOOP);
        Game.soundPlayer.playSound(Sound.TANK_STOP);

        const endReelId: number = path[path.length - 1].x,
            endSymId: number = path[path.length - 1].y;

        // #267
        if (fromBU && ["GE", "XB", "MX", "MX_V_L", "MX_V_R"].includes(lastSymName) && selfReelId === endReelId) {
            return;
        }

        const monsterReelSymbols: Symbol[] = Game.reelsView.reelModels[selfReelId].symbols,
            endReelSymbols: Symbol[] = Game.reelsView.reelModels[endReelId].symbols;

        // #317
        if (!fromBU) {
            this.handleLoopStateWithMixDuration(0, SymbolStates.NORMAL, 0.2);
        }

        // #242
        if (!(selfReelId === endReelId && selfSymId === endSymId)) {
            const indexOfThis: number = monsterReelSymbols.indexOf(this);
            // #273, #297
            if (indexOfThis > -1) {
                const symToReplaceMonster: Symbol = new Symbol("P4", SymbolStates.NORMAL, this.reelPosition);
                symToReplaceMonster.isRemoved = true;
                monsterReelSymbols.splice(indexOfThis, 1, symToReplaceMonster); // remove itself from symbols array of self reel and replace it with dummy symbol to keep the count intact.
            }

            const reelsSymName: string = Game.model.gameData.evaluatedArea[endReelId][endSymId];
            this.reelPosition = endSymId; // change the reel position to have the reel position in the last reel it stops.

            // #462
            if (reelsSymName.includes("MX")) {
                endReelSymbols.push(this);
            } // #273, #297
            else if (!(fromBU && ["GE", "XB"].includes(lastSymName))) {
                endReelSymbols.splice(endReelSymbols.indexOf(Game.reelsView.reelModels[endReelId].getSymbolAt(endSymId, reelsSymName)), 1, this); // add this monster to symbols array of end reel.
            }

            // sort monster reel symbols as per reel position.
            monsterReelSymbols.sort((a: Symbol, b: Symbol) => {
                return (a.reelPosition < b.reelPosition) ? -1 : 1;
            });
            // sort end reel symbols as per reel position.
            endReelSymbols.sort((a: Symbol, b: Symbol) => {
                return (a.reelPosition < b.reelPosition) ? -1 : 1;
            });

            // deliberately changing this
            Game.model.gameData.evaluatedArea[endReelId][endSymId] = this.name;
        }
    }

    private getPathData(toX: number, toY: number, fromX?: number, fromY?: number): PIXI.IPointData[] {
        if (!fromX) {
            fromX = 0;
        }
        if (!fromY) {
            fromY = 0;
        }

        return [{x: fromX, y: fromY}, {x: toX, y: fromY}, {x: toX, y: toY * 0.8}, {x: toX, y: toY}];
    }

    private getAnimTime(animName: string): number {
        if (this.animation && this.animation.state.hasAnimation(animName)) {
            return this.animation.spineData.findAnimation(animName).duration;
        }

        return 0;
    }

    // #337
    private checkNeighboursWhileCollecting(currPos: IMonsterWalk | undefined): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (currPos) {
            const currReel: number = currPos.x,
                currRow: number = currPos.y;

            // left
            if (currReel - 1 > -1) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[currReel - 1][currRow];
                if (reelsSymName !== this.name && Game.MONSTER_SYMBOLS.includes(reelsSymName)) {
                    const symbol: MonsterSymbol = (Game.reelsView.reelModels[currReel - 1].getSymbolAt(currRow, reelsSymName) as MonsterSymbol);
                    tl.add(symbol.getStateAnimation(MONSTER_EXPRESSION.ANGRY, 5), 0);
                }
            }
            // right
            if (currReel + 1 < Game.ACTIVE_REELS_NUM) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[currReel + 1][currRow];
                if (reelsSymName !== this.name && Game.MONSTER_SYMBOLS.includes(reelsSymName)) {
                    const symbol: MonsterSymbol = (Game.reelsView.reelModels[currReel + 1].getSymbolAt(currRow, reelsSymName) as MonsterSymbol);
                    tl.add(symbol.getStateAnimation(MONSTER_EXPRESSION.ANGRY, 5), 0);
                }
            }
            // top
            if (currRow - 1 > -1) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[currReel][currRow - 1];
                if (reelsSymName !== this.name && Game.MONSTER_SYMBOLS.includes(reelsSymName)) {
                    const symbol: MonsterSymbol = (Game.reelsView.reelModels[currReel].getSymbolAt(currRow - 1, reelsSymName) as MonsterSymbol);
                    tl.add(symbol.getStateAnimation(MONSTER_EXPRESSION.ANGRY, 5), 0);
                }
            }
            // bottom
            if (currRow + 1 < Game.ACTIVE_ROWS_NUM) {
                const reelsSymName: string = Game.model.gameData.evaluatedArea[currReel][currRow + 1];
                if (reelsSymName !== this.name && Game.MONSTER_SYMBOLS.includes(reelsSymName)) {
                    const symbol: MonsterSymbol = (Game.reelsView.reelModels[currReel].getSymbolAt(currRow + 1, reelsSymName) as MonsterSymbol);
                    tl.add(symbol.getStateAnimation(MONSTER_EXPRESSION.ANGRY, 5), 0);
                }
            }
        }

        return tl;
    }

    // #317
    private handleLoopStateWithMixDuration(trackIndex: number, state: string, mixDuration: number): void {
        Game.clearPlayingTrack(this.animation, trackIndex);
        this.animation.autoUpdate = true;
        this.animation.state.setAnimation(trackIndex, state, true).mixDuration = mixDuration;
    }

    set grExecuteCount(count: number) {
        this._grExecuteCount = count;
    }

    set isAborted(value: boolean) {
        this._isAborted = value;
    }

    get monsterIdx(): number {
        return this._monsterIdx;
    }
}